import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  CircularProgress,
  Container,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { grey, blueGrey } from '@mui/material/colors';
import Helmet from 'react-helmet';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { AuthQueryName, VERIFY_EMAIL_MUTATION } from '../../gql';
import { Logo } from '../../components';

const containerStyles = { height: '100%', display: 'flex' };
const rootStyles = { width: '100%', height: '100%', background: blueGrey[900] };
const stackStyles = {
  boxShadow: `0 8px 24px ${grey[800]}`,
  width: '100%',
  bgcolor: 'background.paper',
  borderRadius: 2,
};

type State =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success' }
  | { status: 'error'; errors: Array<{ error: string }> };

const VerifyEmail = React.memo(() => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');

  const [state, setState] = useState<State>({ status: 'idle' });
  const startedRef = useRef(false);

  const [verifyEmail] = useMutation(VERIFY_EMAIL_MUTATION, {
    refetchQueries: [AuthQueryName],
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    if (!token) {
      setState({
        status: 'error',
        errors: [{ error: 'Missing verification token.' }],
      });
      return;
    }

    (async () => {
      setState({ status: 'loading' });
      try {
        const { data } = await verifyEmail({ variables: { token } });

        if (data && !data.verifyEmail.errors) {
          setState({ status: 'success' });
          navigate('/');
        } else {
          setState({
            status: 'error',
            errors: data!.verifyEmail!.errors,
          });
        }
      } catch (e) {
        setState({
          status: 'error',
          errors: [{ error: 'Verification failed. Please try again.' }],
        });
      }
    })();
  }, [token, verifyEmail, navigate]);

  return (
    <div style={rootStyles}>
      <Helmet title="Verify e-mail"></Helmet>
      <Container maxWidth="xs" sx={containerStyles}>
        <Stack
          spacing={2}
          alignItems="flex-start"
          justifyContent="center"
          sx={stackStyles}
          px={4}
          pt={8}
          pb={12}
          mt={18}
          mb="auto"
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={2}
            width="100%"
          >
            <Logo size={92} />
            <Typography textAlign="center" fontWeight="bold" variant="h2">
              Acolyte
            </Typography>
          </Stack>

          {(state.status === 'idle' || state.status === 'loading') && (
            <Stack width="100%" alignItems="center" spacing={2}>
              <CircularProgress />
              <Typography>Verifying your e-mail…</Typography>
            </Stack>
          )}

          {state.status === 'success' && (
            <Stack width="100%" spacing={2}>
              <Typography>
                Your e-mail has been verified. Redirecting…
              </Typography>
            </Stack>
          )}

          {state.status === 'error' && (
            <Stack width="100%" spacing={2}>
              <Typography variant="h6" fontWeight="bold">
                Verification failed
              </Typography>
              <Stack>
                {state.errors.map((item: { error: string }) => (
                  <Typography color="red">{item.error}</Typography>
                ))}
              </Stack>
              <Divider />
              <Stack width="100%" alignItems="center">
                <Typography variant="caption">
                  <Link to="/signin" style={{ color: '#FF0070' }}>
                    Sign-in
                  </Link>
                </Typography>
                <Typography variant="caption">
                  <Link to="/" style={{ color: '#D76B90' }}>
                    Home
                  </Link>
                </Typography>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Container>
    </div>
  );
});

export { VerifyEmail };
